import { lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";

const LoginPage = lazy(() => import("@/pages/auth/login.page"));
const VerifyEmailPage = lazy(() => import("@/pages/auth/verify-email.page"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/reset-password.page"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/forgot-password.page"));
const DashboardPage = lazy(() => import("@/pages/dashboard/home/index.page"));
const SettingPage = lazy(() => import("@/pages/dashboard/setting.page"));
const PublicSiteLayout = lazy(() => import("@/components/layout/public-site-layout"));
const LandingPage = lazy(() => import("@/pages/landing/index.page"));
const ExplorePage = lazy(() => import("@/pages/landing/explore.page"));
const AboutPage = lazy(() => import("@/pages/landing/about.page"));
const ContactPage = lazy(() => import("@/pages/landing/contact.page"));
const PricingPage = lazy(() => import("@/pages/landing/pricing.page"));
const PrivacyPage = lazy(() => import("@/pages/landing/privacy.page"));
const TermsPage = lazy(() => import("@/pages/landing/terms.page"));
const StorefrontPage = lazy(() => import("@/pages/landing/storefront.page"));
const StorefrontBookingPage = lazy(() => import("@/pages/landing/booking.page"));
const StorefrontOrderPage = lazy(() => import("@/pages/landing/order.page"));
const PaymentResultPage = lazy(() => import("@/pages/landing/payment-result.page"));
const TrackPage = lazy(() => import("@/pages/landing/track.page"));
const PublicTripsPage = lazy(() => import("@/pages/landing/trips.page"));
const PublicTripDetailPage = lazy(() => import("@/pages/landing/trip-detail.page"));

// Customers
const CustomerListPage = lazy(() => import("@/pages/dashboard/customers/index.page"));
// Front Desk
const FrontDeskPage = lazy(() => import("@/pages/dashboard/front-desk.page"));
// Reservation
const ReservationPage = lazy(() => import("@/pages/dashboard/reservations/index.page"));
const ReservationFormPage = lazy(() => import("@/pages/dashboard/reservations/form.page"));
const ReservationDetailPage = lazy(() => import("@/pages/dashboard/reservations/detail.page"));
// Orders
const OrderListPage = lazy(() => import("@/pages/dashboard/orders/index.page"));
const OrderFormPage = lazy(() => import("@/pages/dashboard/orders/form.page"));
const OrderDetailPage = lazy(() => import("@/pages/dashboard/orders/detail.page"));
const KitchenDisplayPage = lazy(() => import("@/pages/dashboard/kitchen/index.page"));
const AccountingPage = lazy(() => import("@/pages/dashboard/accounting/index.page"));
const InsightsPage = lazy(() => import("@/pages/dashboard/insights/index.page"));
const VatReportPage = lazy(() => import("@/pages/dashboard/vat-report/index.page"));
const StandardViewPage = lazy(() => import("@/pages/dashboard/standard-view/index.page"));
const TransactionsPage = lazy(() => import("@/pages/dashboard/transactions/index.page"));
const AgencyDetailPage = lazy(() => import("@/pages/dashboard/agencies/detail.page"));
const TripFormPage = lazy(() => import("@/pages/dashboard/trips/form.page"));
const ActivityPage = lazy(() => import("@/pages/dashboard/activity/index.page"));

// Inventory
const StockOverviewPage = lazy(() => import("@/pages/dashboard/inventory/overview.page"));
const StockItemListPage = lazy(() => import("@/pages/dashboard/inventory/index.page"));
const StockItemLedgerPage = lazy(() => import("@/pages/dashboard/inventory/ledger.page"));
const StockCategoryListPage = lazy(() => import("@/pages/dashboard/inventory/categories.page"));
const StockLocationListPage = lazy(() => import("@/pages/dashboard/inventory/locations.page"));
const SupplierListPage = lazy(() => import("@/pages/dashboard/inventory/suppliers.page"));
const PurchaseOrderListPage = lazy(() => import("@/pages/dashboard/inventory/purchase-orders.page"));
const StockAlertListPage = lazy(() => import("@/pages/dashboard/inventory/alerts.page"));
const InventoryReportPage = lazy(() => import("@/pages/dashboard/inventory/reports.page"));
const StockCountListPage = lazy(() => import("@/pages/dashboard/inventory/stock-counts.page"));
const StockCountDetailPage = lazy(() => import("@/pages/dashboard/inventory/stock-count.detail.page"));

// Manage
const ManageBusinesslListPage = lazy(() => import("@/pages/dashboard/manage/business/index.page"));
const ManageBusinessDetailPage = lazy(() => import("@/pages/dashboard/manage/business/detail.page"));
const ManageUserListPage = lazy(() => import("@/pages/dashboard/manage/users/index.page"));
const ManageUserDetailPage = lazy(() => import("@/pages/dashboard/manage/users/detail.page"));
const ManageSubscriptionListPage = lazy(() => import("@/pages/dashboard/manage/subscriptions/index.page"));
const ManagePromoListPage = lazy(() => import("@/pages/dashboard/manage/promos/index.page"));
const ManageSubscriptionDetailPage = lazy(() => import("@/pages/dashboard/manage/subscriptions/detail.page"));

// Business
const BusinessPage = lazy(() => import("@/pages/dashboard/business/index.page"));
const FoodMenuListPage = lazy(() => import("@/pages/dashboard/business/food-menu.page"));
const FoodItemListPage = lazy(() => import("@/pages/dashboard/business/food-item.page"));
const FoodToppingListPage = lazy(() => import("@/pages/dashboard/business/food-topping.page"));

// Hotels
const HotelDetailPage = lazy(() => import("@/pages/dashboard/hotels/detail.page"));
const HotelRoolListPage = lazy(() => import("@/pages/dashboard/hotels/rooms/index.page"));
const HotelRoomCategoryListPage = lazy(() => import("@/pages/dashboard/hotels/room-categories/index.page"));
const HotelFoodMenuListPage = lazy(() => import("@/pages/dashboard/hotels/menus/index.page"));
const HotelAmenityListPage = lazy(() => import("@/pages/dashboard/hotels/amenities/index.page"));

// Restaurants
const RestaurantDetailPage = lazy(() => import("@/pages/dashboard/restaurants/detail.page"));
const RestaurantFoodMenuListPage = lazy(() => import("@/pages/dashboard/restaurants/menus/index.page"));
const RestaurantFoodListPage = lazy(() => import("@/pages/dashboard/restaurants/dishes/index.page"));
const RestaurantTableListPage = lazy(() => import("@/pages/dashboard/restaurants/tables/index.page"));

// Roles
const RoleListPage = lazy(() => import("@/pages/dashboard/manage/roles/index.page"));
const RoleDetailpage = lazy(() => import("@/pages/dashboard/manage/roles/detail.page"));

// Invoices
const InvoiceListPage = lazy(() => import("@/pages/dashboard/invoices/index.page"));

// Users
const StaffListPage = lazy(() => import("@/pages/dashboard/staffs/index.page"));
// Calendar
const CalendarPage = lazy(() => import("@/pages/dashboard/calendar/index.page"));

const SubscriptionPage = lazy(() => import("@/pages/dashboard/plan/index.page"));

// Menu Routes
const MenuIndexPage = lazy(() => import("@/pages/menu/index.page"));
const MenuCartPage = lazy(() => import("@/pages/menu/cart.page"));
const MenuOrderPage = lazy(() => import("@/pages/menu/order.page"));

const AuthRoutes = () => {
  const location = useLocation();
  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="*" element={<Navigate to="/auth/login" />} />
    </Routes>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/dashboard">
        <Route index element={<DashboardPage />} />
        <Route path="front-desk" element={<FrontDeskPage />} />

        {/* Reservation Routes */}
        <Route path="reservations">
          <Route index element={<ReservationPage />} />
          <Route path="form" element={<ReservationFormPage />} />
          <Route path="form/:reservationId" element={<ReservationFormPage />} />
          <Route path=":reservationId" element={<ReservationDetailPage />} />
        </Route>

        {/* Order Routes */}
        <Route path="orders">
          <Route index element={<OrderListPage />} />
          <Route path="form" element={<OrderFormPage />} />
          <Route path="form/:orderId" element={<OrderFormPage />} />
          <Route path=":orderId" element={<OrderDetailPage />} />
        </Route>

        {/* Kitchen Display (KOT/BOT) */}
        <Route path="kitchen" element={<KitchenDisplayPage />} />

        {/* Accounting — daybook + expenses */}
        <Route path="accounting" element={<AccountingPage />} />

        {/* Sales insights / reporting */}
        <Route path="insights" element={<InsightsPage />} />

        {/* VAT report (IRD) */}
        <Route path="vat-report" element={<VatReportPage />} />
        <Route path="standard-view" element={<StandardViewPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="agencies/:agencyId" element={<AgencyDetailPage />} />
        <Route path="agencies/:agencyId/trips/form" element={<TripFormPage />} />
        <Route path="agencies/:agencyId/trips/form/:tripId" element={<TripFormPage />} />

        {/* Unified activity log */}
        <Route path="activity" element={<ActivityPage />} />

        {/* Inventory Routes */}
        <Route path="inventory">
          <Route index element={<StockItemListPage />} />
          <Route path="items/:id" element={<StockItemLedgerPage />} />
          <Route path="overview" element={<StockOverviewPage />} />
          <Route path="categories" element={<StockCategoryListPage />} />
          <Route path="locations" element={<StockLocationListPage />} />
          <Route path="suppliers" element={<SupplierListPage />} />
          <Route path="purchase-orders" element={<PurchaseOrderListPage />} />
          <Route path="alerts" element={<StockAlertListPage />} />
          <Route path="reports" element={<InventoryReportPage />} />
          <Route path="counts" element={<StockCountListPage />} />
          <Route path="counts/:id" element={<StockCountDetailPage />} />
        </Route>

        <Route path="calendar" element={<CalendarPage />} />

        {/* Customer Routes */}
        <Route path="customers">
          <Route index element={<CustomerListPage />} />
        </Route>

        <Route path="staffs" element={<StaffListPage />} />
        <Route path="invoices" element={<InvoiceListPage />} />
        <Route path="settings" element={<SettingPage />} />

        {/* Roles — owner-facing (business-scoped). Super-admin also has /dashboard/manage/roles. */}
        <Route path="roles">
          <Route index element={<RoleListPage />} />
          <Route path=":roleId" element={<RoleDetailpage />} />
        </Route>

        {/* Business Routes */}
        <Route path="business">
          <Route index element={<BusinessPage />} />
          <Route path=":businessId" element={<HotelDetailPage />} />
          <Route path=":businessId/food-menus" element={<FoodMenuListPage />} />
          <Route path=":businessId/food-items" element={<FoodItemListPage />} />
          <Route path=":businessId/food-toppings" element={<FoodToppingListPage />} />
        </Route>

        {/* Hotel Routes */}
        <Route path="hotels">
          <Route path=":hotelId" element={<HotelDetailPage />} />
          <Route path=":hotelId/room-categories" element={<HotelRoomCategoryListPage />} />
          <Route path=":hotelId/rooms" element={<HotelRoolListPage />} />
          <Route path=":hotelId/menus" element={<HotelFoodMenuListPage />} />
          <Route path=":hotelId/amenities" element={<HotelAmenityListPage />} />
        </Route>

        {/* Restaurant Routes */}
        <Route path="restaurants">
          <Route path=":restaurantId" element={<RestaurantDetailPage />} />
          <Route path=":restaurantId/tables" element={<RestaurantTableListPage />} />
          <Route path=":restaurantId/menus" element={<RestaurantFoodMenuListPage />} />
          <Route path=":restaurantId/foods" element={<RestaurantFoodListPage />} />
        </Route>

        {/* Manage Routes */}
        <Route path="manage">
          <Route path="business">
            <Route index element={<ManageBusinesslListPage />} />
            <Route path=":businessId" element={<ManageBusinessDetailPage />} />
          </Route>
          <Route path="users">
            <Route index element={<ManageUserListPage />} />
            <Route path=":userId" element={<ManageUserDetailPage />} />
          </Route>
          <Route path="roles">
            <Route index element={<RoleListPage />} />
            <Route path=":roleId" element={<RoleDetailpage />} />
          </Route>
          <Route path="subscriptions">
            <Route index element={<ManageSubscriptionListPage />} />
            <Route path=":subscriptionId" element={<ManageSubscriptionDetailPage />} />
          </Route>
          <Route path="promos" element={<ManagePromoListPage />} />
        </Route>

        {/* Subscription Routes */}
        <Route path="subscription" element={<SubscriptionPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const MenuRoutes = () => {
  const location = useLocation();
  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/a-la-carte-menu">
        <Route path=":token" element={<MenuIndexPage />} />
        <Route path=":token/cart" element={<MenuCartPage />} />
        <Route path=":token/order" element={<MenuOrderPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/a-la-carte-menu" />} />
    </Routes>
  );
};

const LandingRoutes = () => {
  const location = useLocation();
  return (
    <Routes location={location} key={location.pathname}>
      <Route element={<PublicSiteLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/trips" element={<PublicTripsPage />} />
        <Route path="/trips/:id" element={<PublicTripDetailPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/b/:id" element={<StorefrontPage />} />
        <Route path="/b/:id/book" element={<StorefrontBookingPage />} />
        <Route path="/b/:id/order" element={<StorefrontOrderPage />} />
        <Route path="/payment/result" element={<PaymentResultPage />} />
        <Route path="/track" element={<TrackPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export { AuthRoutes, AppRoutes, MenuRoutes, LandingRoutes };
